import { ReactNode } from "react";
import { Control, FieldError, FieldPath, FieldValues } from "react-hook-form";

import AsyncSelect, { Option } from "@components/fields/AsyncSelect";
import useAsyncSearch from "@hooks/useAsyncSearch";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import {
  EmployeeFragment as Employee,
  GetEmployeesDocument,
  GetEmployeesQuery,
  GetEmployeesQueryVariables
} from "@typing/Generated";

type Props<FormData extends FieldValues> = {
  control: Control<FormData>;
  disabled?: boolean;
  employerId: string;
  error: FieldError | undefined;
  initialOption: {
    label: string | undefined;
    value: string | null;
  };
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  onSelection?: (option: { label: string; value: string }) => void;
  required?: boolean;
};

const EmployeeSelect = <FormData extends FieldValues>({
  control,
  disabled = false,
  employerId,
  error,
  initialOption,
  label,
  name,
  onSelection,
  required = false
}: Props<FormData>) => {
  const getEmployees = useLazyQueryWithPromise<GetEmployeesQuery, GetEmployeesQueryVariables>(GetEmployeesDocument);

  const employeeAsyncSearch = useAsyncSearch<Employee>({
    defaultOptions: [],
    getLabel: t => (t ? `${t.firstName} ${t.lastName} (${t.workEmail})` : ""),
    getValue: t => t?.id ?? "",
    initialOption
  });

  return (
    <AsyncSelect<FormData>
      control={control}
      defaultOptions={employeeAsyncSearch.defaultOptions}
      disabled={disabled}
      error={error}
      label={label}
      name={name}
      onSearch={async search =>
        employeeAsyncSearch.searchOptions(search, getEmployees({ variables: { employerId, search } }))
      }
      onSelection={(option: Option) => {
        employeeAsyncSearch.setCurrentOption(option);
        onSelection?.(option);
      }}
      required={required}
      value={employeeAsyncSearch.currentOption}
    />
  );
};

export default EmployeeSelect;
